import React, { useRef } from "react";

import cx from "classnames";
import dayjs from "dayjs";

import { coalesce, maybeDo } from "@kazoohr/helpers";

import { Button } from "../Button";
import { Input } from "../Input";
import { InputProps } from "../Input/Input";

import {
  floorTime,
  getDefaultTime,
  getDropdownItems,
  isInputValid,
  isTweentyFourFormat,
  isTwelveFormat,
  TimeIn12To24,
  timeMask24,
  timeMaskWithPeriod,
  TransformIntoWantedFormat,
} from "./helpers";
import styles from "./TimeInput.scss";

const ARROW_UP = 38;
const ARROW_DOWN = 40;
const ENTER = 13;
const AM_LABEL = "AM";
const PM_LABEL = "PM";
const LABELS = { amLabel: AM_LABEL, pmLabel: PM_LABEL };
const DEFAULT_TIME = getDefaultTime(LABELS);

/**
 * Interface for time items in the dropdown
 */
export interface TimeItemWithIndex {
  twelveHours: string;
  twentyFourHours: string;
  period: string;
  disabled: boolean;
}

/**
 * Handles selection of a time element from the dropdown
 */
export const onSelectElement = (
  idx: number,
  item: TimeItemWithIndex,
  twentyFour: boolean,
  setSelectedPeriod: (period: string) => void,
  setTime: (time: string) => void,
  setHighlightedIdx: (idx: number) => void
): void => {
  setSelectedPeriod(item.period);
  setTime(twentyFour ? item.twentyFourHours : item.twelveHours);
  setHighlightedIdx(idx);
};

/**
 * Scrolls a node into view within a dropdown menu
 */
export const scrollNodeIntoView = (
  nodeRef: HTMLLIElement,
  menu: HTMLUListElement | null
): void => {
  if (!menu) {
    return;
  }

  // If the node is below the visible area, scroll down enough to bring the entire node into view.
  const menuBottomEdge = menu.scrollTop + menu.clientHeight;
  if (nodeRef.offsetTop >= menuBottomEdge) {
    const nodeBottomEdge = nodeRef.offsetTop + nodeRef.offsetHeight;
    menu.scrollTop += nodeBottomEdge - menuBottomEdge;
  }
  // If the node is above the visible area, scroll up to it.
  if (nodeRef.offsetTop < menu.scrollTop) {
    menu.scrollTop = nodeRef.offsetTop;
  }
};

/**
 * Gets the index of the nearest time in the dropdown elements
 */
export const getIndexOfNearestHour = (
  time: string,
  period: string,
  dropdownElements: Array<TimeItemWithIndex>,
  twentyFour: boolean
): number => {
  if (time.length < 5) {
    return -1;
  }
  const nearestTime = floorTime(time);
  const items = dropdownElements;
  if (twentyFour) {
    return items.map((item) => item.twentyFourHours).indexOf(nearestTime);
  }
  return items
    .map((item) => `${item.twelveHours} ${item.period}`)
    .indexOf(`${nearestTime} ${period}`);
};

/**
 * A time input component that supports both 12-hour and 24-hour formats.
 *
 * `onChange` takes a single argument, the 24-hour time representation of the time
 * the user has chosen. This so we can eventually deprecate this component and
 * replace it with native time elements.
 *
 * This takes all of the props from the `Input` component.
 *
 * Planned Deprecation:
 * ```
 * <Input type="time" {...inputProps} />
 * ```
 *
 * Unfortunately, Safari, IE, and the other usual suspects don't support this input
 * type very well, if at all.
 */
export interface TimeInputProps extends Omit<InputProps, "onChange"> {
  /**
   * The ID for the input element
   */
  id: string;

  /**
   * Additional class name for the input element
   */
  inputClassName?: string | false;

  /**
   * Callback function when the time value changes
   */
  onChange: (time: string) => void;

  /**
   * Whether to use 24-hour format. Default: false
   */
  twentyFour?: boolean;

  /**
   * Default value. NOTE: Use this instead of `defaultValue` so the component knows your starting state.
   * Default: current time +30min, rounding to the nearest half hour
   */
  value?: string;

  /**
   * Whether to use compact styling
   */
  compact?: boolean;

  /**
   * Whether the input should take full width
   */
  fullWidth?: boolean;

  /**
   * Whether to show the chevron button
   */
  withChevron?: boolean;

  /**
   * The earliest acceptable time
   */
  firstAcceptableTime?: dayjs.Dayjs;

  /**
   * The timezone to use
   */
  timezone?: string;

  /**
   * Error message
   */
  error?: string;
}

/**
 * Handles input change for time input
 */
export const handleTimeInputChange = (
  time: string,
  twentyFour: boolean,
  LABELS: { amLabel: string; pmLabel: string },
  setTime: (time: string) => void,
  setSelectedPeriod: (period: string) => void,
  AM_LABEL: string,
  PM_LABEL: string
): void => {
  time = time.toUpperCase();
  // We'll change the value only if the input matches the regex pattern
  if (isInputValid(time, twentyFour, LABELS)) {
    let period = time.substr(6, 2);
    if (!period) {
      period = Number(time.substr(0, 2)) < 12 ? AM_LABEL : PM_LABEL;
    }
    time = time.substr(0, 5);
    setTime(time);
    setSelectedPeriod(period);
  }
};

/**
 * Handles button click for time input dropdown
 */
export const handleTimeInputButtonClick = (
  isActionButtonEnabled: boolean,
  setIsDropdownOpen: (isOpen: boolean) => void,
  isDropdownOpen: boolean,
  id: string,
  setisActionButtonEnabled: (isEnabled: boolean) => void
): void => {
  if (isActionButtonEnabled) {
    setIsDropdownOpen(!isDropdownOpen);
    document.getElementById(id)?.focus();
  } else {
    setisActionButtonEnabled(true);
  }
};

/**
 * Handles blur event for time input
 */
export const handleTimeInputBlur = (
  event: React.FocusEvent<HTMLInputElement>,
  isHoverOnElement: boolean,
  chevronId: React.MutableRefObject<number>,
  isDropdownOpen: boolean,
  setisActionButtonEnabled: (isEnabled: boolean) => void,
  setIsDropdownOpen: (isOpen: boolean) => void,
  twentyFour: boolean,
  time: string,
  selectedPeriod: string
): void => {
  // This has to be done, because the blur of the
  // input always triggers before the onClick event in the
  // list items

  if (!isHoverOnElement) {
    // Because the input can be blurred while the dropdown is open or closed
    // the actionButton has to be enabled or disabled accordingly
    // Disabling the button allows the click to activate the blur without triggering
    // the onClickButton(), which triggers after the blur.
    // The chevron is not actually activated when clicked in the down position.
    // The onBlur event is however triggered which has the effect of closing the dropdown.
    // ***
    // Set the value that the input displays always to the time value
    // This because when only set the time when it's valid, causing
    // the input and the time var to be different
    if (
      event.relatedTarget &&
      document.getElementById(`time-picker-caret-btn-${chevronId.current}`) ===
        event.relatedTarget &&
      isDropdownOpen
    ) {
      setisActionButtonEnabled(false);
    }
    setIsDropdownOpen(false);
    event.target.value = twentyFour ? time : `${time} ${selectedPeriod}`;
  }
};

/**
 * Handles key down events for time input
 */
export const handleTimeInputKeyDown = (
  event: React.KeyboardEvent<HTMLInputElement>,
  ARROW_UP: number,
  ARROW_DOWN: number,
  ENTER: number,
  setIsDropdownOpen: (isOpen: boolean) => void,
  isDropdownOpen: boolean,
  dropdownElements: Array<TimeItemWithIndex>,
  highlightedIdx: number | null,
  itemsRef: React.MutableRefObject<Array<HTMLLIElement | null>>,
  dropdownRef: React.MutableRefObject<HTMLUListElement | null>,
  setHighlightedIdx: (idx: number | null) => void,
  twentyFour: boolean,
  setTime: (time: string) => void,
  setSelectedPeriod: (period: string) => void,
  setisActionButtonEnabled: (isEnabled: boolean) => void
): void => {
  if (
    (event.keyCode !== ARROW_UP &&
      event.keyCode !== ARROW_DOWN &&
      event.keyCode !== ENTER) ||
    !event.target
  ) {
    return;
  }

  setIsDropdownOpen(true);
  if (!isDropdownOpen) {
    return;
  }

  if (event.keyCode === ARROW_DOWN) {
    event.preventDefault();
    const items = dropdownElements;
    let index = highlightedIdx === null ? -1 : highlightedIdx;
    index = (index + 1) % items.length;
    if (index > -1 && index !== highlightedIdx) {
      const itemNode = itemsRef.current[index];
      if (itemNode) {
        scrollNodeIntoView(itemNode, dropdownRef.current);
      }
      setHighlightedIdx(index);
    }
  }

  if (event.keyCode === ARROW_UP) {
    event.preventDefault();
    const items = dropdownElements;
    if (highlightedIdx === 0) {
      // clear suggestions if the user taps ArrowUp at the top of the list rather than wrapping (which makes it
      // impossible to "escape" the suggestion list with keyboard input)
      setHighlightedIdx(null);
      return;
    }
    let index = coalesce(highlightedIdx, items.length);
    index = (index - 1) % items.length;
    if (index > -1 && index !== highlightedIdx) {
      const itemNode = itemsRef.current[index];
      if (itemNode) {
        scrollNodeIntoView(itemNode, dropdownRef.current);
      }
      setHighlightedIdx(index);
    }
  }

  if (event.keyCode === ENTER) {
    // You can't select these, but you can up/down to them for accessibility
    // See: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/roles/menuitem_role
    // Note: These shouldn't be menu items anyways, this is built wrong.
    // TODO: WT-19071
    if (highlightedIdx !== null && dropdownElements[highlightedIdx]?.disabled) {
      event.preventDefault();
      return;
    }

    if (highlightedIdx == null) {
      // input has focus but no menu item is selected + enter is hit -> close the menu, highlight whatever's in input
      setIsDropdownOpen(false);
    } else {
      // text entered + menu item has been highlighted + enter is hit -> update value to that of selected menu item, close the menu
      event.preventDefault();
      const item = dropdownElements[highlightedIdx];
      setTime(twentyFour ? item.twentyFourHours : item.twelveHours);
      setSelectedPeriod(item.period);
      setIsDropdownOpen(false);
    }
    setisActionButtonEnabled(true);
  }
};

/**
 * Handles scrolling the highlighted time option into view when the dropdown is opened
 */
export function handleDropdownScroll(
  highlightedIdx: number | null,
  isDropdownOpen: boolean,
  time: string,
  selectedPeriod: string,
  dropdownElements: Array<TimeItemWithIndex>,
  twentyFour: boolean,
  setHighlightedIdx: (idx: number) => void,
  itemsRef: React.MutableRefObject<Array<HTMLLIElement | null>>,
  dropdownRef: React.RefObject<HTMLUListElement>
): void {
  if (highlightedIdx === null) {
    return;
  }
  if (isDropdownOpen) {
    let idx = highlightedIdx;
    // first time clicking the input
    if (idx === -1) {
      idx = getIndexOfNearestHour(
        time,
        selectedPeriod,
        dropdownElements,
        twentyFour
      );
      setHighlightedIdx(idx === -1 ? 0 : idx);
    }
    const itemNode = itemsRef.current[idx];
    if (itemNode) {
      scrollNodeIntoView(itemNode, dropdownRef.current);
    }
  }
}

export function formatTimeDisplay(
  time: string,
  selectedPeriod: string,
  twentyFour: boolean
): string {
  if (twentyFour) {
    return time;
  }

  return `${time} ${selectedPeriod}`;
}

/**
 * A time input component that supports both 12-hour and 24-hour formats
 */
// eslint-disable-next-line max-lines-per-function
export function TimeInput({
  id,
  inputClassName = false,
  onChange,
  twentyFour = false,
  value = DEFAULT_TIME,
  compact,
  fullWidth = false,
  withChevron = true,
  firstAcceptableTime,
  timezone,
  error = undefined,
  ...inputProps
}: TimeInputProps): React.ReactElement {
  let initialValue = value;
  let initialPeriod = PM_LABEL;
  const chevronId = useRef(Math.random());
  // If the value is not in a date format, use the default value
  if (
    !isTwelveFormat(initialValue, LABELS) &&
    !isTweentyFourFormat(initialValue)
  ) {
    initialValue = DEFAULT_TIME;
  }

  [initialValue, initialPeriod] = TransformIntoWantedFormat(
    initialValue,
    twentyFour,
    LABELS
  );

  const [time, setTime] = React.useState(initialValue);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [selectedPeriod, setSelectedPeriod] = React.useState(initialPeriod);
  const [isHoverOnElement, setIsHoverOnElement] = React.useState(false);
  const [isActionButtonEnabled, setisActionButtonEnabled] =
    React.useState(true);

  const testId = coalesce(inputProps["data-testid"], "time-picker-input");

  const inputClassNames = cx(
    styles.input,
    { inputClassName },
    { [styles["input--focused"]]: isDropdownOpen },
    { [styles["chevron-width"]]: withChevron },
    { [styles.fullWidth]: fullWidth }
  );
  const dropDownClassNames = cx(styles["dropdown-menu"], {
    [styles["dropdown-menu--compact"]]: compact,
  });

  // Dropdown
  const dropdownRef = React.useRef<HTMLUListElement>(null);
  const itemsRef = React.useRef<Array<HTMLLIElement | null>>([]);
  const [highlightedIdx, setHighlightedIdx] = React.useState<number | null>(-1);
  const dropdownElements = getDropdownItems(
    LABELS,
    firstAcceptableTime,
    timezone
  );

  const onClick = (): void => {
    setIsDropdownOpen(true);
  };

  React.useEffect(() => {
    setTime(initialValue);
    setSelectedPeriod(initialPeriod);
  }, [initialValue, initialPeriod]);

  React.useEffect(() => {
    handleDropdownScroll(
      highlightedIdx,
      isDropdownOpen,
      time,
      selectedPeriod,
      dropdownElements,
      twentyFour,
      setHighlightedIdx,
      itemsRef,
      dropdownRef
    );
  }, [
    isDropdownOpen,
    highlightedIdx,
    time,
    selectedPeriod,
    dropdownElements,
    twentyFour,
  ]);

  React.useEffect(() => {
    let twentyFourHourTime = time;
    if (!twentyFour) {
      const convertedTime = TimeIn12To24(time, selectedPeriod, LABELS);
      if (convertedTime !== null) {
        twentyFourHourTime = convertedTime;
      }
    }
    onChange(twentyFourHourTime);
    setIsDropdownOpen(false);
    setisActionButtonEnabled(true);
  }, [time, selectedPeriod]);

  const input = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Input
        {...inputProps}
        id={id}
        compact={compact}
        className={inputClassNames}
        onChange={(value) =>
          handleTimeInputChange(
            value,
            twentyFour,
            LABELS,
            setTime,
            setSelectedPeriod,
            AM_LABEL,
            PM_LABEL
          )
        }
        onKeyDown={(e) =>
          handleTimeInputKeyDown(
            e,
            ARROW_UP,
            ARROW_DOWN,
            ENTER,
            setIsDropdownOpen,
            isDropdownOpen,
            dropdownElements,
            highlightedIdx,
            itemsRef,
            dropdownRef,
            setHighlightedIdx,
            twentyFour,
            setTime,
            setSelectedPeriod,
            setisActionButtonEnabled
          )
        }
        onClick={onClick}
        fullWidth={fullWidth}
        onMouseEnter={() => setIsHoverOnElement(false)}
        value={formatTimeDisplay(time, selectedPeriod, twentyFour)}
        onBlur={(event) =>
          handleTimeInputBlur(
            event,
            isHoverOnElement,
            chevronId,
            isDropdownOpen,
            setisActionButtonEnabled,
            setIsDropdownOpen,
            twentyFour,
            time,
            selectedPeriod
          )
        }
        mask={twentyFour ? timeMask24 : timeMaskWithPeriod}
        defaultValue={twentyFour ? time : `${time} ${selectedPeriod}`}
        guide={false}
        data-testid={testId}
        autoComplete="off"
        aria-expanded={isDropdownOpen}
        aria-autocomplete="list"
        iconPosition="right"
        error={error}
      />
      <Button
        id={`time-picker-caret-btn-${chevronId.current}`}
        data-testid="time-picker-caret-btn-test"
        type="button"
        icon={
          withChevron ? (isDropdownOpen ? "caret_up" : "caret_dn") : undefined
        }
        size="default"
        className={styles["trigger-button-icon"]}
        color="var(--confetti-text-secondary)"
        onClick={() =>
          handleTimeInputButtonClick(
            isActionButtonEnabled,
            setIsDropdownOpen,
            isDropdownOpen,
            id,
            setisActionButtonEnabled
          )
        }
      />
    </div>
  );

  return (
    <div className={styles.wrapper}>
      {input}
      {isDropdownOpen && (
        <TimeInputDropdown
          dropdownRef={dropdownRef}
          itemsRef={itemsRef}
          dropDownClassNames={dropDownClassNames}
          setIsHoverOnElement={setIsHoverOnElement}
          dropdownElements={dropdownElements}
          highlightedIdx={highlightedIdx}
          twentyFour={twentyFour}
          setSelectedPeriod={setSelectedPeriod}
          setTime={setTime}
          setHighlightedIdx={setHighlightedIdx}
        />
      )}
    </div>
  );
}

/**
 * Props for the TimeInputDropdown component
 */
interface TimeInputDropdownProps {
  dropdownRef: React.RefObject<HTMLUListElement>;
  itemsRef: React.MutableRefObject<Array<HTMLLIElement | null>>;
  dropDownClassNames: string;
  setIsHoverOnElement: (isHover: boolean) => void;
  dropdownElements: Array<TimeItemWithIndex>;
  highlightedIdx: number | null;
  twentyFour: boolean;
  setSelectedPeriod: (period: string) => void;
  setTime: (time: string) => void;
  setHighlightedIdx: (idx: number) => void;
}

/**
 * Dropdown component for TimeInput
 */
export const TimeInputDropdown = ({
  dropdownRef,
  itemsRef,
  dropDownClassNames,
  setIsHoverOnElement,
  dropdownElements,
  highlightedIdx,
  twentyFour,
  setSelectedPeriod,
  setTime,
  setHighlightedIdx,
}: TimeInputDropdownProps): React.ReactElement => {
  return (
    <ul
      ref={dropdownRef}
      className={dropDownClassNames}
      data-testid="time-picker-dropdown"
      aria-labelledby="timeSelector"
      onMouseEnter={() => setIsHoverOnElement(true)}
      onMouseLeave={() => setIsHoverOnElement(false)}
    >
      {dropdownElements.map((time, idx) => {
        return (
          <li
            key={`time-picker-${idx}`}
            ref={(el) => (itemsRef.current[idx] = el)}
            onClick={() =>
              maybeDo(
                !time.disabled,
                onSelectElement,
                idx,
                time,
                twentyFour,
                setSelectedPeriod,
                setTime,
                setHighlightedIdx
              )
            }
            className={cx(styles["dropdown-item"], {
              [styles["dropdown-item--focused"]]: idx === highlightedIdx,
              [styles["dropdown-item--disabled"]]: time.disabled,
            })}
            aria-disabled={coalesce(time.disabled, undefined)}
            role="menuitem"
            tabIndex={-1}
            data-testid={`time-picker-item-${idx}`}
          >
            {twentyFour
              ? time.twentyFourHours
              : `${time.twelveHours} ${time.period}`}
          </li>
        );
      })}
    </ul>
  );
};

export default TimeInput;
